import { forwardRef } from 'react';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button, ButtonProps } from '../../../components/ui/button';

interface AddLineButtonProps extends Omit<ButtonProps, 'children'> {
  /**
   * Already-translated label, e.g. `t('sales:line.addLine')` or `t('common:app.addLine')`.
   * Keep it a short verb phrase ("Add line", "Add item").
   */
  label: string;
}

/**
 * Standardized "+ Add line" affordance for adding a row to an inline line-item editor
 * on a detail page (purchase order, invoice, sales order, shipment cargo/contents, deal
 * quote, …).
 *
 * Use this INSTEAD of an always-present empty editable add-row at the bottom of the table —
 * an empty editable row reads as a spurious blank line. The standard pattern is: render the
 * existing rows, then this button; clicking it either appends a new editable line or reveals
 * a compose row. See `docs/design-system/ui-list-pages.md` → "Line-item editor tables".
 *
 * Defaults to a full-width dashed outline bar that sits at the bottom of the editor, so the
 * affordance looks identical across every document's line-item table. Plus icon = creation
 * (a new line is created), consistent with the STYLE_GUIDE action-terminology rules.
 *
 * @example
 * <AddLineButton label={t('sales:line.addLine')} onClick={() => append(newLine())} />
 *
 * // Inside a raw table, span all columns:
 * <TableRow>
 *   <TableCell colSpan={8} className="p-2">
 *     <AddLineButton label={t('shipments:detail.cargo.addCargo')} onClick={() => setAddRowOpen(true)} />
 *   </TableCell>
 * </TableRow>
 */
export const AddLineButton = forwardRef<HTMLButtonElement, AddLineButtonProps>(
  ({ label, className, variant = 'outline', size = 'sm', ...props }, ref) => {
    return (
      <Button
        ref={ref}
        type="button"
        variant={variant}
        size={size}
        className={cn(
          'w-full justify-center border-dashed text-muted-foreground hover:text-foreground',
          className
        )}
        {...props}
      >
        <Plus className="h-4 w-4 me-1.5" />
        {label}
      </Button>
    );
  }
);

AddLineButton.displayName = 'AddLineButton';
